import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardPlus, MessageCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { SegmentedTabs } from "../../components/family/SegmentedTabs";
import { useFamilyAuth } from "../../context/FamilyAuthContext";
import {
  FAMILY_RICHIESTA_CATEGORIE,
  familyRichiestaCategoria,
  familyRichiestaCodice,
  familyRichiestaDate,
  familyRichiestaStato,
  familyRichiestaWhen,
} from "../../utils/familyRichieste";

function Card({ children, className = "" }) {
  return <section className={`family-card ${className}`}>{children}</section>;
}

function Loading() {
  return <div className="py-20 text-center text-sm text-[#72839a]">Caricamento delle informazioni…</div>;
}

function Message({ error }) {
  return error ? <p className="family-card border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</p> : null;
}

function EmptyState({ title, text }) {
  return (
    <Card className="px-5 py-10 text-center">
      <MessageCircle className="mx-auto h-8 w-8 text-[#c5b7de]" />
      <p className="mt-3 font-extrabold text-[#304762]">{title}</p>
      <p className="mt-1 text-sm text-[#72839a]">{text}</p>
    </Card>
  );
}

function StatusDot({ stato }) {
  const [, label, dot, text] = familyRichiestaStato(stato);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${text}`}>
      <i className={`h-2 w-2 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function PageHeader({ title, subtitle, action, backTo = "/family/altro" }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div className="flex min-w-0 gap-3">
        <Link to={backTo} className="mt-0.5 rounded-full p-1 text-[#314b6c] hover:bg-white"><ArrowLeft className="h-5 w-5" /></Link>
        <div>
          <h1 className="text-[22px] font-extrabold tracking-tight text-[#243952] sm:text-2xl">{title}</h1>
          {subtitle ? <p className="mt-0.5 text-xs text-[#687c94] sm:text-sm">{subtitle}</p> : null}
        </div>
      </div>
      {action}
    </div>
  );
}

export default function FamilyRichieste() {
  const { richiestaId } = useParams();
  const navigate = useNavigate();
  const { activeOspiteId, hasPermission, api } = useFamilyAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("tutte");
  const [searchParams] = useSearchParams();
  const prefillPai = searchParams.get("nuova") === "pai" && hasPermission("richieste:write");
  const [open, setOpen] = useState(prefillPai);
  const [form, setForm] = useState(prefillPai
    ? { categoria: "documenti", oggetto: "Richiesta copia del PAI", messaggio: "Buongiorno, vorrei ricevere una copia del Piano Assistenziale Individualizzato. Grazie." }
    : { categoria: "altro", oggetto: "", messaggio: "" });
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const loadList = async () => {
    if (!activeOspiteId) { setLoading(false); return; }
    setLoading(true);
    try {
      const response = await api.get(`/ospiti/${activeOspiteId}/richieste`);
      setItems(response.data?.items || []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Non è stato possibile caricare i messaggi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); }, [activeOspiteId]);

  useEffect(() => {
    if (!richiestaId) { setDetail(null); return undefined; }
    let cancelled = false;
    api.get(`/richieste/${richiestaId}`)
      .then((response) => { if (!cancelled) { setDetail(response.data); setError(""); } })
      .catch((err) => { if (!cancelled) { setDetail(null); setError(err.response?.data?.detail || "Messaggio non trovato"); } });
    return () => { cancelled = true; };
  }, [api, richiestaId]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true); setError("");
    try {
      await api.post(`/ospiti/${activeOspiteId}/richieste`, form);
      setOpen(false);
      setForm({ categoria: "altro", oggetto: "", messaggio: "" });
      toast.success("Richiesta inviata alla struttura");
      await loadList();
    } catch (err) {
      setError(err.response?.data?.detail || "Non è stato possibile inviare la richiesta");
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!richiestaId || !reply.trim()) return;
    setSaving(true); setError("");
    try {
      await api.post(`/richieste/${richiestaId}/messaggi`, { messaggio: reply.trim() });
      setReply("");
      toast.success("Messaggio inviato");
      const response = await api.get(`/richieste/${richiestaId}`);
      setDetail(response.data);
      await loadList();
    } catch (err) {
      setError(err.response?.data?.detail || "Non è stato possibile inviare il messaggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !richiestaId) return <Loading />;

  if (richiestaId) {
    const richiesta = detail?.richiesta;
    const messaggi = detail?.messaggi || [];
    return (
      <div className="space-y-4">
        <PageHeader
          backTo="/family/richieste"
          title={richiesta ? familyRichiestaCodice(richiesta) : "Messaggio"}
          subtitle={richiesta ? familyRichiestaCategoria(richiesta.categoria) : "Dettaglio della richiesta"}
        />
        <Message error={error} />
        {!richiesta && !error ? <Loading /> : null}
        {richiesta ? (
          <>
            <Card className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] text-[#8a9bb0]">{familyRichiestaDate(richiesta.created_at)}</p>
                  <h2 className="mt-1 font-extrabold text-[#304762]">{richiesta.oggetto}</h2>
                </div>
                <StatusDot stato={richiesta.stato} />
              </div>
            </Card>
            <div className="space-y-3">
              {messaggi.map((message) => (
                <Card key={message.id} className={`p-4 ${message.autore_tipo === "struttura" ? "bg-[#fcfbfe]" : ""}`}>
                  <p className="text-[11px] font-semibold text-[#6e48b5]">
                    {message.autore_tipo === "struttura" ? "Struttura" : "Tu"} · {familyRichiestaWhen(message.created_at)}
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-[#60758f]">{message.messaggio}</p>
                </Card>
              ))}
            </div>
            {richiesta.stato === "chiusa" ? (
              <p className="text-sm text-[#72839a]">Questa richiesta è chiusa. Per un nuovo argomento apri una nuova richiesta.</p>
            ) : hasPermission("richieste:write") ? (
              <Card className="p-4">
                <form onSubmit={sendReply} className="space-y-3">
                  <Textarea required rows={3} aria-label="Aggiungi un messaggio" placeholder="Aggiungi un messaggio" value={reply} onChange={(e) => setReply(e.target.value)} />
                  <Button disabled={saving || !reply.trim()} className="h-10 w-full rounded-xl bg-[#6e48b5] hover:bg-[#5a3b94]">{saving ? "Invio…" : "Invia"}</Button>
                </form>
              </Card>
            ) : null}
          </>
        ) : null}
      </div>
    );
  }

  const openCount = items.filter((item) => item.stato !== "chiusa").length;
  const requests = items.filter((item) => filter === "tutte" || (filter === "aperte" ? item.stato !== "chiusa" : item.stato === "chiusa"));
  return (
    <div className="space-y-4">
      <PageHeader
        title="Messaggi"
        subtitle="Come possiamo aiutarti?"
        action={hasPermission("richieste:write") ? (
          <Button onClick={() => setOpen(!open)} className="h-9 rounded-xl bg-[#6e48b5] px-3 hover:bg-[#5a3b94]">
            <ClipboardPlus className="mr-1 h-4 w-4" />Nuova richiesta
          </Button>
        ) : null}
      />
      <SegmentedTabs idPrefix="richieste" ariaLabel="Filtro richieste" tabs={[{ value: "tutte", label: "Tutte" }, { value: "aperte", label: "Aperte", badge: openCount || null }, { value: "chiuse", label: "Chiuse" }]} value={filter} onChange={setFilter} />
      <Message error={error} />
      {open ? (
        <Card className="p-4">
          <form onSubmit={submit} className="space-y-3">
            <select aria-label="Categoria della richiesta" className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
              {FAMILY_RICHIESTA_CATEGORIE.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <Input required minLength={3} aria-label="Oggetto della richiesta" placeholder="Oggetto della richiesta" value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })} />
            <Textarea required minLength={2} aria-label="Testo della richiesta" placeholder="Scrivi la tua richiesta" value={form.messaggio} onChange={(e) => setForm({ ...form, messaggio: e.target.value })} />
            <Button disabled={saving} className="h-10 w-full rounded-xl bg-[#6e48b5] hover:bg-[#5a3b94]">{saving ? "Invio…" : "Invia richiesta"}</Button>
          </form>
        </Card>
      ) : null}
      {requests.length ? (
        <div className="space-y-2">
          {requests.map((item) => (
            <button key={item.id} type="button" onClick={() => navigate(`/family/richieste/${item.id}`)} className="block w-full text-left">
              <Card className="flex items-start gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2 text-[11px] text-[#8a9bb0]">
                    <span>{familyRichiestaCodice(item)}</span>
                    <span>{familyRichiestaDate(item.created_at)}</span>
                  </div>
                  <p className="mt-1 truncate text-sm font-extrabold text-[#304762]">{item.oggetto || familyRichiestaCategoria(item.categoria)}</p>
                  {item.preview ? <p className="mt-1 line-clamp-2 text-sm text-[#60758f]">“{item.preview}”</p> : null}
                  <div className="mt-2"><StatusDot stato={item.stato} /></div>
                </div>
              </Card>
            </button>
          ))}
        </div>
      ) : error ? null : (
        <EmptyState title="Nessun messaggio" text={filter === "tutte" ? "Quando invierai una richiesta alla struttura, la vedrai qui." : "Nessuna richiesta in questa sezione."} />
      )}
    </div>
  );
}
